import { Component, Input, Output, EventEmitter, forwardRef } from '@angular/core';

import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { HugeiconsIconComponent } from '@hugeicons/angular';
import { CloudUploadIcon, Cancel01Icon, ViewIcon } from '@hugeicons/core-free-icons';

// File01Icon inlined — the barrel re-export resolves to undefined at runtime due to
// ESM/CJS interop in the Angular Vite build. Typed to match IconSvgObject exactly.
const File01Icon: [string, { [key: string]: string | number }][] = [
    ['path', { d: 'M8 7L16 7', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '1.5', key: '0' }],
    ['path', { d: 'M8 11L12 11', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '1.5', key: '1' }],
    ['path', { d: 'M13 21.5V21C13 18.1716 13 16.7574 13.8787 15.8787C14.7574 15 16.1716 15 19 15H19.5M20 13.3431V10C20 6.22876 20 4.34315 18.8284 3.17157C17.6569 2 15.7712 2 12 2C8.22877 2 6.34315 2 5.17157 3.17157C4 4.34314 4 6.22876 4 10L4 14.5442C4 17.7892 4 19.4117 4.88607 20.5107C5.06508 20.7327 5.26731 20.9349 5.48933 21.1139C6.58831 22 8.21082 22 11.4558 22C12.1614 22 12.5141 22 12.8372 21.886C12.9044 21.8623 12.9702 21.835 13.0345 21.8043C13.3436 21.6564 13.593 21.407 14.0919 20.9081L18.8284 16.1716C19.4065 15.5935 19.6955 15.3045 19.8478 14.9369C20 14.5694 20 14.1606 20 13.3431Z', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '1.5', key: '2' }]
];

@Component({
    selector: 'app-file-upload',
    standalone: true,
    imports: [HugeiconsIconComponent],
    templateUrl: './file-upload.component.html',
    styleUrl: './file-upload.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FileUploadComponent),
            multi: true
        }
    ]
})
export class FileUploadComponent implements ControlValueAccessor {
    @Input() label: string = '';
    @Input() accept: string = '*/*';
    @Input() multiple: boolean = false;
    @Input() hint: string = '';
    @Input() required: boolean = false;
    @Input() allowNone: boolean = false;
    @Input() hasError: boolean = false;

    // Icons
    uploadIcon = CloudUploadIcon;
    fileIcon = File01Icon;
    closeIcon = Cancel01Icon;
    viewIcon = ViewIcon;

    selectedFiles: File[] = [];
    isDragging = false;
    isNone = false;

    private onChange = (value: any) => { };
    private onTouched = () => { };

    onDragOver(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging = true;
    }

    onDragLeave(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging = false;
    }

    onDrop(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging = false;

        if (event.dataTransfer?.files.length) {
            this.handleFiles(event.dataTransfer.files);
        }
    }

    onFileSelected(event: any) {
        if (event.target.files.length) {
            this.handleFiles(event.target.files);
        }
    }

    handleFiles(fileList: FileList | File[]) {
        const files = Array.from(fileList);
        if (this.multiple) {
            this.selectedFiles = [...this.selectedFiles, ...files];
            this.onChange(this.selectedFiles);
        } else {
            this.selectedFiles = [files[0]];
            this.onChange(files[0]);
        }
        this.isNone = false;
        this.onTouched();
    }

    removeFile(index: number, event: Event) {
        event.stopPropagation();
        if (this.multiple) {
            this.selectedFiles.splice(index, 1);
            this.onChange(this.selectedFiles);
        } else {
            this.selectedFiles = [];
            this.onChange(null);
        }
    }

    toggleNone(event: any) {
        this.isNone = event.target.checked;
        if (this.isNone) {
            this.selectedFiles = [];
            this.onChange('none');
        } else {
            this.onChange(this.multiple ? [] : null);
        }
    }

    previewFile(file: File, event: Event): void {
        event.stopPropagation();
        const url = URL.createObjectURL(file);
        window.open(url, '_blank');
    }

    formatSize(size: number): string {
        if (size < 1024) return size + ' B';
        if (size < 1048576) return (size / 1024).toFixed(1) + ' KB';
        return (size / 1048576).toFixed(1) + ' MB';
    }

    // ControlValueAccessor methods
    writeValue(value: any): void {
        if (value === 'none') {
            this.isNone = true;
            this.selectedFiles = [];
        } else if (Array.isArray(value)) {
            this.selectedFiles = value;
            this.isNone = false;
        } else if (value instanceof File) {
            this.selectedFiles = [value];
            this.isNone = false;
        } else if (typeof value === 'string' && value !== 'none') {
            // Probably a URL or path, we can't easily turn it back to a File object here
            // but we can at least show it if we enhance the component to handle strings.
            // For now, let's just clear.
            this.selectedFiles = [];
            this.isNone = false;
        } else {
            this.selectedFiles = [];
            this.isNone = false;
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }
}
